<template>
  <div class="tab-panel-body">
    <!-- Header -->
    <div class="config-section-header">
      <div class="config-section-header__left">
        <v-icon small class="mr-2" color="text_secondary">mdi-image-multiple</v-icon>
        <span class="config-section-label">Telas de descanso</span>
        <v-chip v-if="telas_descanso && telas_descanso.length" x-small class="ml-2" color="surface_tertiary">
          {{ telas_descanso.length }} / 5
        </v-chip>
      </div>
      <ModalAdicionarTelaDescanso
        :cod_producao="codProducao"
        :getTelasDescanso="fetch"
      />
    </div>

    <!-- Grid -->
    <div v-if="telas_descanso && telas_descanso.length" class="descanso-grid">
      <div
        v-for="tela in telas_descanso"
        :key="tela.id_tela_descanso"
        class="descanso-card"
        :class="{ 'descanso-card-active': tela.flg_selecionada }"
      >
        <div class="descanso-image-container">
          <v-img
            class="descanso-image"
            :src="tela.url_midia"
            aspect-ratio="1.6"
            cover
          >
            <template v-slot:placeholder>
              <div class="d-flex align-center justify-center fill-height">
                <v-progress-circular indeterminate size="24" color="primary" />
              </div>
            </template>

            <!-- Overlay actions -->
            <div class="descanso-overlay">
              <ModalExcluirTelaDescanso
                :fetchData="fetch"
                :cod_producao="codProducao"
                :id_tela_descanso="tela.id_tela_descanso"
              />
            </div>
          </v-img>
        </div>

        <div class="descanso-card-footer">
          <v-btn
            v-if="loadingAtivar === tela.id_tela_descanso"
            :loading="true"
            text
            x-small
            color="primary"
          ></v-btn>
          <v-btn
            v-else
            text
            x-small
            :color="tela.flg_selecionada ? 'primary' : 'text_secondary'"
            @click="selecionarTelaDescanso(!tela.flg_selecionada, tela)"
            class="descanso-select-btn"
          >
            <v-icon x-small class="mr-1">
              {{ tela.flg_selecionada ? 'mdi-check-circle' : 'mdi-circle-outline' }}
            </v-icon>
            {{ tela.flg_selecionada ? 'Padrão' : 'Definir como padrão' }}
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-if="telas_descanso && telas_descanso.length === 0" class="sc-empty-state">
      <v-icon size="48" color="text_secondary" class="mb-3">mdi-image-off-outline</v-icon>
      <span class="sc-empty-state__title">Nenhuma tela de descanso</span>
      <span class="sc-empty-state__subtitle">Adicione imagens para exibir quando não há conteúdo no mosaico</span>
    </div>
  </div>
</template>

<script>
import { apiClient } from "@/services/api";
import { mapActions, mapGetters } from "vuex";
import { StorageKeys, setItem, removeItem } from "@/services/storage";
import ModalAdicionarTelaDescanso from "../ModalAdicionarTelaDescanso.vue";
import ModalExcluirTelaDescanso from "../ModalExcluirTelaDescanso.vue";

export default {
  name: "ConfigTabTelaDescanso",
  components: { ModalAdicionarTelaDescanso, ModalExcluirTelaDescanso },
  props: {
    codProducao: { type: Number, required: true },
    active: { type: Boolean, default: false },
  },
  watch: {
    active: {
      immediate: true,
      handler(isActive) {
        if (isActive) this.fetch();
      },
    },
    codProducao() {
      if (this.active) this.fetch();
    },
  },
  data() {
    return {
      loadingAtivar: null,
    };
  },
  computed: {
    ...mapGetters({
      telas_descanso: "telas_descanso/getTelasDescanso",
    }),
  },
  methods: {
    ...mapActions({
      getTelasDescansos: "telas_descanso/getTelasDescansos",
    }),
    async fetch() {
      await this.getTelasDescansos({ cod_producao: this.codProducao });
    },
    async selecionarTelaDescanso(flg_selecionada, tela_descanso_obj) {
      try {
        this.loadingAtivar = tela_descanso_obj.id_tela_descanso;
        const response = await apiClient.patch(`/selecionarProducaoTelaDescanso`, {
          id_tela_descanso: tela_descanso_obj.id_tela_descanso,
          cod_producao: this.codProducao,
          flg_selecionada,
        });
        if (response.status === 200) {
          if (flg_selecionada === true) {
            setItem(StorageKeys.TELA_DESCANSO_ATIVA, response.data.tela_descanso);
          } else {
            removeItem(StorageKeys.TELA_DESCANSO_ATIVA);
          }
          await this.fetch();
        }
        this.loadingAtivar = null;
      } catch (error) {
        this.$toast.error("Erro ao atualizar tela de descanso.");
        this.loadingAtivar = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* Grid */
.descanso-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

/* Card */
.descanso-card {
  background: var(--v-surface_tertiary-base);
  border-radius: var(--sc-radius-lg);
  overflow: hidden;
  border: 1px solid transparent;
  transition: all var(--sc-duration-base) var(--sc-easing-standard);

  &:hover {
    border-color: var(--sc-border-emphasis);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }
}

.descanso-card-active {
  border-color: rgba(var(--sc-color-primary-rgb),0.25);
}

/* Image */
.descanso-image-container {
  position: relative;
}

.descanso-image {
  border-radius: var(--sc-radius-lg) var(--sc-radius-lg) 0 0;
}

.descanso-overlay {
  position: absolute;
  top: 8px;
  right: 8px;
  opacity: 0;
  transition: opacity var(--sc-duration-base) var(--sc-easing-standard);
}

.descanso-image-container:hover .descanso-overlay {
  opacity: 1;
}

/* Footer */
.descanso-card-footer {
  padding: 8px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.descanso-select-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 500 !important;
}

</style>
